// The voices are different on the different OS plus they
// are also different on every browser
// Paste this in the browser's console to check the array of supported voices
// JSON.stringify(
//   speechSynthesis.getVoices().map(v => ({
//     name: v.name,
//     lang: v.lang,
//     localService: v.localService,
//     default: v.default,
//     voiceURI: v.voiceURI,
//   }))
// );

export const defaultLangCode = "EN";

// The only portable contract of the Web Speech API is SpeechSynthesisVoice.lang (BCP-47).
export const langByCode = {
  EN: "en-GB",
  FR: "fr-FR",
  DE: "de-DE",
  ES: "es-ES",
  PL: "pl-PL",
  BG: "bg-BG",
  TR: "tr-TR",
};

export const preferredVoices = {
  "en-GB": {
    male: ["Google UK English Male", "Daniel", "Microsoft George"],
    female: ["Google UK English Female", "Kate", "Serena", "Microsoft Hazel"],
  },
  "fr-FR": {
    male: ["Thomas", "Microsoft Paul", "Google français"],
    female: ["Amélie", "Audrey", "Microsoft Julie", "Google français"],
  },
  "de-DE": {
    male: ["Markus", "Microsoft Stefan", "Google Deutsch"],
    female: ["Anna", "Petra", "Microsoft Hedda", "Google Deutsch"],
  },
  "es-ES": {
    male: ["Jorge", "Microsoft Pablo", "Google español"],
    female: ["Mónica", "Marisol", "Microsoft Helena", "Google español"],
  },
  "pl-PL": {
    male: ["Krzysztof", "Microsoft Adam", "Google polski"],
    female: ["Zosia", "Ewa", "Microsoft Paulina", "Google polski"],
  },
  "bg-BG": {
    male: ["Microsoft Ivan", "Daria"],
    female: ["Daria", "Microsoft Ekaterina"],
  },
  "tr-TR": {
    male: ["Microsoft Tolga", "Microsoft Ahmet", "Yelda"],
    female: ["Yelda", "Microsoft Emel"],
  },
};

const langPrefixPattern = /^\s*\[([A-Z]{2})\]/;

// Messages are prefixed by the AI with "[XX] ". Anything else falls back to the default
// language instead of blindly reading two characters off the front of the string.
export function detectLangCode(text: ?string): string {
  if (!text) {
    return defaultLangCode;
  }

  const match = langPrefixPattern.exec(text);

  if (!match || !langByCode[match[1]]) {
    return defaultLangCode;
  }

  return match[1];
}

// Firefox on Linux reports "fr_FR" instead of "fr-FR".
function normalizeLang(lang: ?string): string {
  return String(lang || "")
    .toLowerCase()
    .replace("_", "-");
}

function findByPreferredName(voices: Array<Object>, names: Array<string>, base: string) {
  for (let i = 0; i < names.length; i++) {
    const wanted = names[i].toLowerCase();
    const match = voices.find(
      v =>
        normalizeLang(v.lang).split("-")[0] === base &&
        String(v.name || "")
          .toLowerCase()
          .indexOf(wanted) !== -1,
    );

    if (match) {
      return match;
    }
  }

  return null;
}

export function pickVoice(voices: ?Array<Object>, bcp47: string, gender: string): Object | null {
  if (!voices || !voices.length) {
    return null;
  }

  const norm = normalizeLang(bcp47);
  const base = norm.split("-")[0];
  const preferred = (preferredVoices[bcp47] || {})[gender] || [];

  const byName = findByPreferredName(voices, preferred, base);

  if (byName) {
    return byName;
  }

  const exact = voices.filter(v => normalizeLang(v.lang) === norm);

  if (exact.length) {
    return exact.find(v => v.localService) || exact[0];
  }

  const byPrefix = voices.find(v => {
    const lang = normalizeLang(v.lang);
    return lang === base || lang.indexOf(`${base}-`) === 0;
  });

  if (byPrefix) {
    return byPrefix;
  }

  return null;
}

const voicesTimeoutMs = 1500;

let cachedVoices = null;
let pendingVoices = null;
let invalidatorAttached = false;

function getSynth() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.speechSynthesis || null;
}

const waiters = [];

// Voices can appear at runtime (Chrome loads its remote list lazily, the OS may install a
// language pack), so a single subscription refreshes the cache and wakes up any waiters.
function attachInvalidator(synth) {
  if (invalidatorAttached) {
    return;
  }

  invalidatorAttached = true;

  const handler = () => {
    const voices = synth.getVoices();

    if (voices && voices.length) {
      cachedVoices = voices;
    }

    waiters.splice(0, waiters.length).forEach(waiter => waiter());
  };

  if (synth.addEventListener) {
    synth.addEventListener("voiceschanged", handler);
  } else {
    synth.onvoiceschanged = handler;
  }
}

// Synchronous read for use inside a click handler — speak() must stay in the user gesture.
export function getCachedVoices(): Array<Object> {
  const synth = getSynth();

  if (!synth) {
    return [];
  }

  const voices = synth.getVoices();

  if (voices && voices.length) {
    cachedVoices = voices;
    return voices;
  }

  return cachedVoices || [];
}

// getVoices() is asynchronous in Chrome/Edge — empty on the first tick after a page load.
export function getVoicesAsync(): Promise<Array<Object>> {
  const synth = getSynth();

  if (!synth) {
    return Promise.resolve([]);
  }

  attachInvalidator(synth);

  const immediate = getCachedVoices();

  if (immediate.length) {
    return Promise.resolve(immediate);
  }

  if (pendingVoices) {
    return pendingVoices;
  }

  pendingVoices = new Promise(resolve => {
    let timer = null;
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timer);
      pendingVoices = null;
      resolve(getCachedVoices());
    };

    waiters.push(finish);
    // Resolve with whatever we have (even []) so the caller never hangs.
    timer = setTimeout(finish, voicesTimeoutMs);
  });

  return pendingVoices;
}
